import { useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import { db } from "../../services/firebase";
import { incidentTypeLabel, STATUS_LABELS } from "./incidentConstants";

function timestampMillis(value) {
  if (typeof value?.toMillis === "function") return value.toMillis();
  return 0;
}

export default function DriverIncidentList() {
  const { userInfo } = useContext(UserContext);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userInfo?.uid) return undefined;
    const incidentQuery = query(
      collection(db, "incidents"),
      where("reporter.uid", "==", userInfo.uid),
    );
    return onSnapshot(
      incidentQuery,
      (snapshot) => {
        setIncidents(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (nextError) => {
        console.error("Failed to load incidents:", nextError);
        setError("Could not load your incident reports.");
        setLoading(false);
      },
    );
  }, [userInfo?.uid]);

  const sortedIncidents = useMemo(
    () => [...incidents].sort((a, b) => timestampMillis(b.updatedAt) - timestampMillis(a.updatedAt)),
    [incidents],
  );

  return (
    <main className="w-full px-4 py-5 sm:px-6 xl:px-8">
      <div className="mx-auto max-w-6xl space-y-5">
        <header className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">Driver</p>
            <h1 className="mt-1 text-2xl font-semibold text-gray-950">Incident reports</h1>
            <p className="mt-1 text-sm text-gray-600">Start a report, continue a draft, or check its status.</p>
          </div>
          <Link
            to="/driver/incidents/new"
            className="inline-flex items-center justify-center rounded-xl bg-gray-950 px-4 py-2.5 text-sm font-semibold text-white no-underline hover:bg-black"
          >
            Report an incident
          </Link>
        </header>

        {error && <Message tone="error">{error}</Message>}
        {loading ? (
          <Message>Loading incident reports...</Message>
        ) : sortedIncidents.length === 0 ? (
          <Message>You have not created an incident report yet.</Message>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="divide-y divide-gray-100">
              {sortedIncidents.map((incident) => {
                const section2 = incident.partA?.section2 || {};
                return (
                  <Link
                    key={incident.id}
                    to={`/driver/incidents/${incident.id}`}
                    className="flex flex-col gap-2 px-5 py-4 text-gray-900 no-underline transition hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="font-semibold">
                        {incident.incidentNumber || "Draft incident report"}
                      </div>
                      <div className="mt-1 text-sm text-gray-600">
                        {incidentTypeLabel(section2.incidentType)}
                        {incident.partA?.section1?.occurredDate
                          ? ` · ${incident.partA.section1.occurredDate}`
                          : ""}
                      </div>
                    </div>
                    <span className="inline-flex w-fit rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                      {STATUS_LABELS[incident.workflowStatus] || incident.workflowStatus}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

function Message({ children, tone = "default" }) {
  return (
    <div className={`rounded-2xl border px-5 py-6 text-sm ${tone === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-gray-200 bg-white text-gray-600"}`}>
      {children}
    </div>
  );
}
